import { withOnClickTracking } from 'auto-tracking';
import { ComponentPropsWithoutRef, forwardRef, PropsWithChildren } from 'react';

import { H4, P } from '../../components/text';
import { styled, theme } from '../../stitches.config';
import { rem } from '../../util';
import { BasicButton, basicButtonStyles } from '../basic-button';
import { Spinner } from '../spinner';

const itemViewContainerBaseStyle = {
  textAlign: 'left',
  boxSizing: 'border-box',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: rem(8),
  padding: `${rem(8)} ${rem(16)}`,
};
export const ListItem = withOnClickTracking(
  styled(
    'li',
    Object.assign({}, itemViewContainerBaseStyle, {
      listStyle: 'none',
      margin: 0,
      fontWeight: 500,
    }),
  ),
);
ListItem.displayName = 'ListItem';

const ListItemLabelView = styled(P, {
  flex: 1,
});
ListItemLabelView.displayName = 'ListItemLabelView';
export const ListItemButton = withOnClickTracking(
  styled(
    forwardRef<
      HTMLButtonElement,
      ComponentPropsWithoutRef<typeof BasicButton> & { loading?: boolean }
    >(({ loading, disabled, children, ...forwardProps }, ref) => {
      const isDisabled = loading ?? disabled ?? false;
      const isLoading = loading ?? false;
      const content = (
        <>
          {children}
          {isLoading && (
            <ListItemRight>
              <Spinner size={16} />
            </ListItemRight>
          )}
        </>
      );
      return (
        <button disabled={isDisabled} {...forwardProps} ref={ref}>
          {content}
        </button>
      );
    }),
    {
      ...basicButtonStyles,
      ...itemViewContainerBaseStyle,
      width: `calc(100% + ${rem(32)})`,
      margin: `-${rem(8)} -${rem(16)}`,
      variants: {
        selected: {
          true: {
            'a&': {
              color: theme.colors.blue12,
            },
            [`& ${ListItemLabelView}`]: {
              color: `${theme.colors.blue12}`,
            },
            color: theme.colors.blue12,
            backgroundColor: theme.colors.gray4,
          },
        },
      },
      'a&': {
        color: 'currentcolor',
        textDecoration: 'none',
      },
      [`&:disabled ${P}`]: {
        color: theme.colors.gray6,
        cursor: 'default',
      },
      ['&:hover:enabled, a&:hover']: {
        backgroundColor: theme.colors.gray3,
      },
      ['&:active:enabled, a&:active']: {
        backgroundColor: theme.colors.gray4,
      },
    },
  ),
);
ListItemButton.displayName = 'ListItemButton';
export const List = styled('ul', {
  variants: {
    size: {
      big: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: '16px',
        [`& ${ListItemButton}`]: {
          borderRadius: '6px',
        },
      },
    },
  },
  defaultVariants: { size: 'regular' },
  listStyle: 'none',
  padding: 0,
  margin: 0,
});
List.displayName = 'List';

export const ListItemLabel = (props: PropsWithChildren<unknown>) => {
  return <ListItemLabelView as={'div'}>{props.children}</ListItemLabelView>;
};
export const ListItemLeft = styled('div', {
  minWidth: 24,
  flexShrink: 0,
});
ListItemLeft.displayName = 'ListItemLeft';
export const ListItemRight = styled('div', {
  marginLeft: 'auto',
  textAlign: 'right',
  minWidth: 40,
  flexShrink: 0,
});
ListItemRight.displayName = 'ListItemRight';
export const ListSubHeader = (props: PropsWithChildren<unknown>) => {
  return (
    <ListItem>
      <H4>{props.children}</H4>
    </ListItem>
  );
};
